import {
    ExportOutlined,
    InfoCircleOutlined,
    PlusOutlined,
    SearchOutlined,
    ToolOutlined,
} from "@ant-design/icons";
import {
    Button,
    Checkbox,
    Col,
    Form,
    Input,
    Popover,
    Radio,
    Row,
    Select,
    Space,
    Tooltip,
} from "antd";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import ActionDefinitionStore from "../stores/ActionDefinitionStore";
import {
    ActionDefinition,
    ActionField,
    ActionType,
    PayloadStructure,
} from "../types/Actions";
import ActionFieldInputSetupDialog from "./ActionFieldInputSetupDialog";
import ActionFieldEditor from "./ActionFieldEditor";
import ConstraintEditor from "./ConstraintEditor";
import { Utils } from "../../common/misc/Utils";
import PayloadStructureDialog from "./PayloadStructureDialog";
import ActionHelper from "src/modules/common/misc/ActionHelper";
import { getActionDefinitionDefaults } from "../misc/actionDefinitionDefaults";

const FormItem = Form.Item;
const PREDEFINED_FIELD_TYPE: ActionField["fieldType"] = "FixedValue";

type Props = {
    store: ActionDefinitionStore;
};

const ActionDefinitionForm: React.FC<Props> = ({ store }) => {
    const [form] = Form.useForm();
    const mode = store.editDialogMode;
    const [isTypeSet, setIsTypeSet] = useState<boolean>(false);
    const [selectedActionType, setSelectedActionType] = useState<
        ActionType | undefined
    >(store.currentActionType);
    const [permissionMode, setPermissionMode] = useState<
        "anyProjectUser" | "adminsOnly" | "selectedRoles"
    >("anyProjectUser");
    const [isCustomPayload, setIsCustomPayload] = useState<boolean>(
        (store.selectedDefinition?.payloadStructure ?? null) !== null,
    );

    const getSampleJSON = (payloadStructure: PayloadStructure | null) => {
        if (payloadStructure) {
            return JSON.stringify(
                ActionHelper.generateSampleJSON(payloadStructure),
                null,
                2,
            );
        }
        return undefined;
    };

    const [sampleJson, setSampleJson] = useState<string | undefined>(
        getSampleJSON(store.selectedDefinition?.payloadStructure ?? null),
    );
    const watchedFields = Form.useWatch("fields", form) as
        | ActionField[]
        | undefined;
    const watchedConstraints = Form.useWatch("constraints", form) as
        | unknown[]
        | undefined;
    const hasAiProjectLookupField =
        selectedActionType?.fields?.some(
            (field) => field.name === "aiProjectId",
        ) ?? false;
    const hasAiApplicationLookupField =
        selectedActionType?.fields?.some((field) => field.name === "aiAppId") ??
        false;
    const hasAiLookupField =
        hasAiProjectLookupField || hasAiApplicationLookupField;
    const selectedAiProjectId = watchedFields?.find(
        (field) => field?.name === "aiProjectId",
    )?.defaultValue;
    const normalizePredefinedFields = (fields?: ActionField[]) =>
        fields?.map((field) =>
            field.isPredefined && !field.fieldType
                ? {
                      ...field,
                      fieldType: PREDEFINED_FIELD_TYPE,
                  }
                : field,
        ) ?? [];
    const getPermissionMode = (
        permissions?: ActionDefinition["permissions"],
    ) => {
        if ((permissions?.roles?.length ?? 0) > 0) {
            return "selectedRoles";
        }

        if (permissions?.admin) {
            return "adminsOnly";
        }

        return "anyProjectUser";
    };

    React.useEffect(() => {
        if (!store.editDialogVisible) {
            form.resetFields();
            setPermissionMode("anyProjectUser");
        }
    }, [store.editDialogVisible, form]);

    React.useEffect(() => {
        if (store.selectedDefinition) {
            form.setFieldsValue({
                ...store.selectedDefinition,
                permissions: store.selectedDefinition.permissions ?? {
                    admin: false,
                    roles: [],
                },
                fields: normalizePredefinedFields(
                    store.selectedDefinition.fields,
                ),
            });
            setIsTypeSet(
                store.selectedDefinition.actionType !== undefined &&
                    store.selectedDefinition.actionType.length > 0,
            );
            setIsCustomPayload(
                (store.selectedDefinition?.payloadStructure ?? null) !== null,
            );
            setSampleJson(
                getSampleJSON(
                    store.selectedDefinition?.payloadStructure ?? null,
                ),
            );
            setSelectedActionType(store.currentActionType);
            setPermissionMode(
                getPermissionMode(store.selectedDefinition.permissions),
            );
        }
    }, [store.selectedDefinition?.id, store.editDialogVisible]);

    React.useEffect(() => {
        if (!store.editDialogVisible || !hasAiLookupField) {
            store.clearAiApplications();
            return;
        }

        if (hasAiProjectLookupField || hasAiApplicationLookupField) {
            void store.ensureAiProjectsLoaded();
        }

        if (hasAiApplicationLookupField && selectedAiProjectId) {
            void store.loadAiApplications(selectedAiProjectId);
            return;
        }

        store.clearAiApplications();
    }, [
        hasAiApplicationLookupField,
        hasAiLookupField,
        hasAiProjectLookupField,
        selectedAiProjectId,
        store,
        store.editDialogVisible,
    ]);

    const handleSubmit = () => {
        form.validateFields()
            .then(() => {
                const formData = form.getFieldsValue();
                formData.permissions = formData.permissions ?? {
                    admin: false,
                    roles: [],
                };

                if (mode === "create") {
                    store.createActionDefinition(formData);
                } else {
                    store.updateActionDefinition(
                        formData.actionType,
                        formData.name,
                        formData.title,
                        formData.fields,
                        formData.constraints,
                        formData.isConfirmationRequired,
                        formData.permissions,
                        formData.payloadStructure,
                    );
                }
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleActionTypeChange = (newValue: string) => {
        const actionType = store.actionTypes.find((x) => x.name === newValue);

        if (actionType) {
            const nextDefaults = getActionDefinitionDefaults(actionType);

            setIsTypeSet(true);
            setSelectedActionType(actionType);

            if (nextDefaults) {
                form.setFieldValue("name", nextDefaults.name);
                form.setFieldValue("title", nextDefaults.title);
            }

            let currentFields: ActionField[] =
                form.getFieldValue("fields") || [];
            const requiredFields = actionType?.fields;
            currentFields = currentFields.filter((f) => !f.isPredefined);

            if (requiredFields && requiredFields.length > 0) {
                const missing = requiredFields
                    ?.filter(
                        (x) =>
                            currentFields?.find(
                                (c: ActionField) => c.name === x.name,
                            ) === undefined,
                    )
                    .map((x) => ({
                        ...x,
                        isPredefined: true,
                        fieldType: PREDEFINED_FIELD_TYPE,
                    }));

                if (missing && missing.length > 0) {
                    form.setFieldValue("fields", [
                        ...missing,
                        ...currentFields,
                    ]);
                }
            } else {
                form.setFieldValue("fields", currentFields);
            }
        } else {
            setIsTypeSet(false);
            setSelectedActionType(undefined);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const applyPermissionMode = (
        mode: "anyProjectUser" | "adminsOnly" | "selectedRoles",
    ) => {
        setPermissionMode(mode);

        if (mode === "anyProjectUser") {
            form.setFieldValue(["permissions", "admin"], false);
            form.setFieldValue(["permissions", "roles"], []);
            return;
        }

        if (mode === "adminsOnly") {
            form.setFieldValue(["permissions", "admin"], true);
            form.setFieldValue(["permissions", "roles"], []);
            return;
        }

        form.setFieldValue(["permissions", "admin"], false);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const actionNameValidator = (rule: any, value: any, callback: any) => {
        const actions = store!.actionDefinitions.map((a) =>
            a.name.toLowerCase(),
        );
        if (
            value &&
            actions.includes(value.toLowerCase()) &&
            store.selectedDefinition?.name.toLowerCase() !== value.toLowerCase()
        ) {
            callback("Action with the same name already exists");
        } else {
            callback();
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const actionTitleValidator = (rule: any, value: any, callback: any) => {
        const actions = store!.actionDefinitions.map((a) =>
            a.title?.toLowerCase(),
        );
        if (
            value &&
            actions.includes(value.toLowerCase()) &&
            store.selectedDefinition?.title?.toLowerCase() !==
                value.toLowerCase()
        ) {
            callback("Action with the same title already exists");
        } else {
            callback();
        }
    };

    const actionTypes = Utils.groupBy(
        store.actionTypes
            .slice()
            .sort((a, b) => a.title.localeCompare(b.title)),
        (k) => (k.isAttachment ? "Attachment" : "Task"),
    );

    const actionTypeOptions = Object.keys(actionTypes).map((t) => ({
        label: t,
        options: actionTypes[t as "Attachment" | "Task"].map((a) => ({
            label: a.title,
            value: a.name,
        })),
    }));
    return (
        <div className="dialog-section">
            <Form
                form={form}
                onFinish={handleSubmit}
                className="alpha-form"
                id="edit-action-definition-form"
                layout="vertical"
            >
                <FormItem name="projectId" noStyle>
                    <Input type="hidden" />
                </FormItem>
                <FormItem name={["permissions", "admin"]} noStyle>
                    <Input type="hidden" />
                </FormItem>
                <Row gutter={24}>
                    <Col span={8}>
                        <FormItem
                            name="actionType"
                            colon={false}
                            className="dialog-field"
                            label={
                                <span className="dialog-field-label">
                                    Action type
                                </span>
                            }
                            rules={[
                                {
                                    required: true,
                                    message: "Action type is required",
                                },
                            ]}
                        >
                            <Select
                                suffixIcon={
                                    <i
                                        className="alpha-icon xxs arrow-down-icon"
                                        style={{ margin: 0, width: 8 }}
                                    />
                                }
                                onChange={handleActionTypeChange}
                                options={actionTypeOptions}
                            />
                        </FormItem>
                    </Col>
                    <Col span={5} className="action-confirmation-col">
                        <Form.Item
                            className="action-confirmation-item"
                            name="isConfirmationRequired"
                            valuePropName="checked"
                        >
                            <Checkbox>
                                Ask for confirmation before action
                            </Checkbox>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={6}>
                        <FormItem
                            name="title"
                            colon={false}
                            className="dialog-field"
                            label={
                                <span className="dialog-field-label">
                                    Action label
                                </span>
                            }
                            rules={[{ validator: actionTitleValidator }]}
                        >
                            <Input />
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem
                            name="name"
                            colon={false}
                            className="dialog-field"
                            label={
                                <span className="dialog-field-label">
                                    System name
                                </span>
                            }
                            rules={[
                                {
                                    required: true,
                                    message: "System name is required",
                                },
                                { validator: actionNameValidator },
                            ]}
                        >
                            <Input disabled={mode === "edit"} />
                        </FormItem>
                    </Col>
                </Row>
                {isTypeSet ? (
                    <>
                        <div className="modal-section-title">
                            <span>Action fields</span>
                        </div>
                        {selectedActionType?.supportsCustomPayload && (
                            <Form.Item
                                label="Custom payload"
                                name="payloadStructure"
                            >
                                <Space size="small">
                                    <Button
                                        type={
                                            isCustomPayload
                                                ? "primary"
                                                : "default"
                                        }
                                        icon={<ToolOutlined />}
                                        onClick={() =>
                                            store.setEditPayloadDialogVisible(
                                                true,
                                                form.getFieldValue(
                                                    "payloadStructure",
                                                ),
                                                (
                                                    structure: PayloadStructure | null,
                                                ) => {
                                                    form.setFieldValue(
                                                        "payloadStructure",
                                                        structure,
                                                    );
                                                    setIsCustomPayload(
                                                        structure !== null,
                                                    );
                                                    setSampleJson(
                                                        getSampleJSON(
                                                            structure,
                                                        ),
                                                    );
                                                },
                                                form
                                                    .getFieldValue("fields")
                                                    ?.map(
                                                        (f: ActionField) =>
                                                            f.name,
                                                    ),
                                            )
                                        }
                                    >
                                        Configure
                                    </Button>
                                    {isCustomPayload && sampleJson && (
                                        <Popover
                                            content={
                                                <pre>
                                                    <code>{sampleJson}</code>
                                                </pre>
                                            }
                                            title="Sample payload"
                                            placement="right"
                                            fresh={false}
                                            trigger="click"
                                        >
                                            <Button
                                                type="default"
                                                icon={<SearchOutlined />}
                                            >
                                                Preview
                                            </Button>
                                        </Popover>
                                    )}
                                </Space>
                            </Form.Item>
                        )}
                        <div className="action-fields-grid-header">
                            <span className="action-fields-column action-fields-column-title">
                                Field label
                            </span>
                            <span className="action-fields-column action-fields-column-name">
                                System name
                            </span>
                            <span className="action-fields-column action-fields-column-type">
                                Type
                            </span>
                            <span className="action-fields-column action-fields-column-default">
                                Value input
                            </span>
                            <span className="action-fields-column action-fields-column-required">
                                Required
                            </span>
                            <span className="action-fields-column action-fields-column-actions" />
                        </div>
                        <Form.List name="fields">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name }) => (
                                        <ActionFieldEditor
                                            key={key}
                                            store={store}
                                            index={name}
                                            form={form}
                                            actionType={selectedActionType}
                                            remove={remove}
                                        />
                                    ))}
                                    <Form.Item>
                                        <Button
                                            type="link"
                                            className="inline-add-button"
                                            onClick={() =>
                                                add({ isPredefined: false })
                                            }
                                            icon={<PlusOutlined />}
                                        >
                                            Add field
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                        <div className="modal-section-title">
                            <span>Constraints</span>
                        </div>
                        <div className="action-permissions-panel">
                            <div className="action-permissions-panel-title">
                                Who can run this action?
                            </div>
                            <div className="action-permissions-options">
                                <Button
                                    type="text"
                                    className={`action-permissions-option ${permissionMode === "anyProjectUser" ? "selected" : ""}`}
                                    onClick={() =>
                                        applyPermissionMode("anyProjectUser")
                                    }
                                >
                                    <Radio
                                        checked={
                                            permissionMode === "anyProjectUser"
                                        }
                                    />
                                    <div className="modal-option-label">
                                        Any project user
                                    </div>
                                </Button>
                                <Button
                                    type="text"
                                    className={`action-permissions-option ${permissionMode === "adminsOnly" ? "selected" : ""}`}
                                    onClick={() =>
                                        applyPermissionMode("adminsOnly")
                                    }
                                >
                                    <Radio
                                        checked={
                                            permissionMode === "adminsOnly"
                                        }
                                    />
                                    <div className="modal-option-label">
                                        Project Admins only
                                    </div>
                                </Button>
                                <div
                                    className={`action-permissions-option with-selector ${permissionMode === "selectedRoles" ? "selected" : ""}`}
                                    onClick={() =>
                                        applyPermissionMode("selectedRoles")
                                    }
                                >
                                    <div className="action-permissions-option-header">
                                        <Radio
                                            checked={
                                                permissionMode ===
                                                "selectedRoles"
                                            }
                                        />
                                        <div className="modal-option-label">
                                            <Space size={6}>
                                                <span>
                                                    Selected action roles
                                                </span>
                                                <Tooltip title="Action roles only control who can run actions. They do not change project or system access.">
                                                    <InfoCircleOutlined className="action-access-title-icon" />
                                                </Tooltip>
                                            </Space>
                                        </div>
                                    </div>
                                    {permissionMode === "selectedRoles" ? (
                                        <>
                                            <Form.Item
                                                className="action-permissions-select"
                                                name={["permissions", "roles"]}
                                            >
                                                <Select
                                                    mode="multiple"
                                                    placeholder="Select action roles"
                                                    options={
                                                        store.actionRoleOptions
                                                    }
                                                    onClick={(event) =>
                                                        event.stopPropagation()
                                                    }
                                                />
                                            </Form.Item>
                                            <Button
                                                type="link"
                                                className="action-permissions-manage-link"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    void store.openActionRolesDialog();
                                                }}
                                            >
                                                Manage action roles
                                                <ExportOutlined />
                                            </Button>
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        {(watchedConstraints?.length ?? 0) > 0 && (
                            <div className="constraints-grid-header">
                                <span className="constraints-column constraints-column-kind">
                                    <span className="constraints-header-with-tooltip">
                                        Constraint type
                                        <Popover
                                            content={
                                                <span>
                                                    Include -{">"} Action runs
                                                    only if condition is met.
                                                    Exclude -{">"}
                                                    Action is blocked if
                                                    condition is met
                                                </span>
                                            }
                                            trigger="hover"
                                        >
                                            <InfoCircleOutlined className="constraints-header-info" />
                                        </Popover>
                                    </span>
                                </span>
                                <span className="constraints-column constraints-column-field-type">
                                    Field type
                                </span>
                                <span className="constraints-column constraints-column-name">
                                    Field name
                                </span>
                                <span className="constraints-column constraints-column-operator">
                                    Operator
                                </span>
                                <span className="constraints-column constraints-column-value">
                                    Value
                                </span>
                                <span className="constraints-column constraints-column-actions" />
                            </div>
                        )}
                        <Form.List name="constraints">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name }) => (
                                        <ConstraintEditor
                                            key={key}
                                            store={store}
                                            index={name}
                                            form={form}
                                            remove={remove}
                                            isAttachment={
                                                selectedActionType?.isAttachment ??
                                                false
                                            }
                                        />
                                    ))}
                                    <Form.Item>
                                        <Button
                                            type="link"
                                            className="inline-add-button"
                                            onClick={() =>
                                                add({ kind: "Requirement" })
                                            }
                                            icon={<PlusOutlined />}
                                        >
                                            Add constraint
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                    </>
                ) : null}
            </Form>
            <ActionFieldInputSetupDialog store={store} />
            <PayloadStructureDialog store={store} />
        </div>
    );
};

export default observer(ActionDefinitionForm);
